// src/pages/purchase-requisition/components/OrderTable.jsx
import React, { useState, useMemo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, Box, Typography, Tooltip,
    TablePagination
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { formatDateTime, formatDate, formatTime, getDayName } from "../history-pengajuan/utils/formatters";
import { UserTooltipDisplay } from "../PengajuanBarang";

/**
 * Renders the table displaying purchase orders.
 * @param {object} props - Component props.
 * @param {Array} props.orders - List of order data.
 * @param {Array} props.flowConfigurations - List of flow configurations.
 * @param {object} props.user - Authenticated user object.
 * @param {object} props.userProfile - User's profile data.
 * @param {string} props.prcnInvMaterialPackPHRoleId - Role ID for "Prcn. & Inv. Material Pack PH".
 * @param {string} props.invManagementFreshPineOtherRoleId - Role ID for "Inv Management FreshPine & Other".
 * @param {function} props.handleViewOrder - Handler to view order details.
 * @param {function} props.handleOpenConfirmDialog - Handler to open approval/rejection confirmation dialog.
 * @param {function} props.handleResubmitOrder - Handler to resubmit a rejected order.
 * @param {function} props.handleDownloadExcel - Handler to download order data to Excel.
 * @param {function} props.getUomName - Helper function to get UoM name.
 */
const OrderTable = ({
    orders,
    flowConfigurations,
    user,
    userProfile,
    prcnInvMaterialPackPHRoleId,
    invManagementFreshPineOtherRoleId,
    handleViewOrder,
    handleOpenConfirmDialog,
    handleResubmitOrder,
    handleDownloadExcel,
    getUomName,
    calculateCumulativeInterval
}) => {
    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // State untuk tooltip copy
    const [copyTooltip, setCopyTooltip] = useState("Copy ID Order");

    // Filter out orders with "Approved" status
    const filteredOrders = useMemo(() => {
        return orders.filter(order => order.status !== "Approved");
    }, [orders]);

    // Handlers for pagination changes
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handler untuk copy ID Order
    const handleCopyId = (orderId) => {
        navigator.clipboard.writeText(orderId)
            .then(() => {
                setCopyTooltip("ID Order dicopy!");
                setTimeout(() => setCopyTooltip("Copy ID Order"), 2000);
            })
            .catch((err) => {
                console.error("Gagal menyalin ID Order:", err);
                setCopyTooltip("Gagal menyalin!");
                setTimeout(() => setCopyTooltip("Copy ID Order"), 2000);
            });
    };

    // Fungsi untuk mendapatkan informasi tanggal pengajuan
    const getSubmissionInfo = (order) => {
        if (!order || !order.created_at) {
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
        
        try {
            const submissionDate = new Date(order.created_at);
            
            return {
                date: formatDate(submissionDate, 'DD/MM/YYYY'),
                day: getDayName(submissionDate),
                time: formatTime(submissionDate, 'HH.mm.ss'),
                fullDate: submissionDate
            };
        } catch (error) {
            console.error("Error formatting submission date:", error);
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
    };

    // Fungsi untuk format interval
    const formatInterval = (diffMs) => {
        if (diffMs <= 0) return "0 D";
        
        const totalSeconds = Math.floor(diffMs / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const remainingSeconds = totalSeconds % 86400;
        const hours = Math.floor(remainingSeconds / 3600);
        const remainingMinutesSeconds = remainingSeconds % 3600;
        const minutes = Math.floor(remainingMinutesSeconds / 60);
        const seconds = remainingMinutesSeconds % 60;
        
        let result = [];
        if (days > 0) {
            result.push(`${days} H`); // H untuk hari
        }
        if (hours > 0) {
            result.push(`${hours} J`); // J untuk jam
        }
        if (minutes > 0) {
            result.push(`${minutes} M`); // M untuk menit
        }
        if (seconds > 0) {
            result.push(`${seconds} D`); // D untuk detik
        }
        
        return result.length > 0 ? result.join(', ') : "0 D";
    };

    // Fungsi untuk menghitung interval
    const calculateFormattedInterval = (order) => {
        if (!order || !order.created_at) {
            return "-";
        }

        try {
            let endTime;
            const startTime = new Date(order.created_at);
            
            if (order.status === "Approved") {
                // Untuk approved, hitung sampai approval terakhir
                if (order.approval_history && order.approval_history.length > 0) {
                    const approvedEntries = order.approval_history.filter(entry => 
                        entry.decision === "Approved"
                    );
                    
                    if (approvedEntries.length > 0) {
                        approvedEntries.sort((a, b) => 
                            new Date(b.timestamp) - new Date(a.timestamp)
                        );
                        const finalApproval = approvedEntries[0];
                        endTime = new Date(finalApproval.timestamp);
                    }
                }
                
                if (!endTime && order.updated_at) {
                    endTime = new Date(order.updated_at);
                }
            } else if (order.status === "Rejected") {
                // Untuk rejected, hitung sampai rejection
                if (order.approval_history && order.approval_history.length > 0) {
                    const rejectedEntry = order.approval_history.find(entry => 
                        entry.decision === "Rejected"
                    );
                    if (rejectedEntry && rejectedEntry.timestamp) {
                        endTime = new Date(rejectedEntry.timestamp);
                    }
                }
                
                if (!endTime && order.updated_at) {
                    endTime = new Date(order.updated_at);
                }
            } else {
                // Untuk pending, hitung sampai step terakhir yang di-approve
                if (order.approval_history && order.approval_history.length > 0) {
                    const approvedEntries = order.approval_history.filter(entry => 
                        entry.decision === "Approved"
                    );
                    
                    if (approvedEntries.length > 0) {
                        // Ambil approval terakhir
                        approvedEntries.sort((a, b) => 
                            new Date(b.timestamp) - new Date(a.timestamp)
                        );
                        const latestApproval = approvedEntries[0];
                        endTime = new Date(latestApproval.timestamp);
                    }
                }
            }
            
            // Jika tidak ada approval_history, gunakan waktu sekarang untuk pending
            if (!endTime) {
                endTime = new Date();
            }
            
            const diffMs = endTime - startTime;
            
            return formatInterval(diffMs);
            
        } catch (error) {
            console.error("Error calculating formatted interval:", error);
            return "-";
        }
    };

    // Slice the filtered orders array for current page display
    const paginatedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Order Title</TableCell>
                        <TableCell>Submission Date</TableCell>
                        <TableCell>Requested By</TableCell>
                        <TableCell>Interval</TableCell>
                        <TableCell>Total Items</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedOrders.length > 0 ? (
                        paginatedOrders.map((order) => {
                            const flowConfig = flowConfigurations.find(f => f.id === order.flow_config_id);
                            const approvalStepsArray = flowConfig ? Object.values(flowConfig.approval_steps).sort((a, b) => a.order - b.order) : [];
                            const currentStep = approvalStepsArray[order.current_approval_step_index];

                            const isMyTurnToApprove = userProfile && currentStep &&
                                order.status === "Pending Approval" &&
                                (currentStep.order - 1) === order.current_approval_step_index &&
                                (userProfile.id_role &&
                                Array.isArray(currentStep.target_role_ids) &&
                                userProfile.id_role.split(';').some(userRole => currentStep.target_role_ids.includes(userRole)));

                            const canResubmit = order.status === "Rejected" && order.created_by_uid === user.uid;

                            // Check if user has either "Prcn. & Inv. Material Pack PH" OR "Inv Management FreshPine & Other" role
                            const userHasRelevantDownloadRole =
                                (prcnInvMaterialPackPHRoleId && userProfile.id_role && userProfile.id_role.split(';').includes(prcnInvMaterialPackPHRoleId)) ||
                                (invManagementFreshPineOtherRoleId && userProfile.id_role && userProfile.id_role.split(';').includes(invManagementFreshPineOtherRoleId));

                            // PERUBAHAN: Tombol download muncul jika user memiliki role yang relevan
                            // (tidak peduli status order apa pun)
                            const canDownload = userHasRelevantDownloadRole;

                            // Dapatkan informasi tanggal pengajuan
                            const submissionInfo = getSubmissionInfo(order);

                            return (
                                <TableRow key={order.id}>
                                    {/* ID */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Tooltip title={copyTooltip} placement="top">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopyId(order.id)}
                                                    sx={{ 
                                                        padding: '4px',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>

                                    {/* Order Title */}
                                    <TableCell>{order.order_title || '-'}</TableCell>

                                    {/* Submission Date */}
                                    <TableCell>
                                        <Tooltip 
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2"><strong>Hari:</strong> {submissionInfo.day}</Typography>
                                                    <Typography variant="body2"><strong>Waktu:</strong> {submissionInfo.time}</Typography>
                                                </Box>
                                            }
                                            placement="bottom"
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                {submissionInfo.date}
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    {/* Requested By */}
                                    <TableCell>
                                        <UserTooltipDisplay
                                            uid={order.created_by_uid}
                                            userName={order.created_by_name}
                                        />
                                    </TableCell>

                                    {/* Interval */}
                                    <TableCell>{calculateFormattedInterval(order)}</TableCell>
                                    
                                    {/* Total Items */}
                                    <TableCell>{order.item_details?.length || 0} items</TableCell>

                                    {/* Status */}
                                    <TableCell>
                                        <Chip
                                            label={order.status === "Pending Approval" && currentStep ? `Menunggu: ${currentStep.step_name}` : order.status}
                                            color={
                                                order.status === "Approved" ? "success" :
                                                order.status === "Rejected" ? "error" : "warning"
                                            }
                                            size="small"
                                        />
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleViewOrder(order)}>
                                            <VisibilityOutlinedIcon />
                                        </IconButton>
                                        {isMyTurnToApprove && (
                                            <>
                                                <IconButton size="small" color="success" onClick={() => handleOpenConfirmDialog(order.id, "Approved")}>
                                                    <CheckCircleOutlineIcon />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleOpenConfirmDialog(order.id, "Rejected")}>
                                                    <CancelOutlinedIcon />
                                                </IconButton>
                                            </>
                                        )}
                                        {canResubmit && (
                                            <IconButton size="small" color="info" onClick={() => handleResubmitOrder(order)}>
                                                <RefreshIcon />
                                            </IconButton>
                                        )}
                                        {canDownload && (
                                            <IconButton size="small" color="primary" onClick={() => handleDownloadExcel(order)}>
                                                <FileDownloadIcon />
                                            </IconButton>
                                        )}
                                    </TableCell>

                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={8} align="center">Tidak ada permintaan order.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Baris per halaman:"
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`
                }
            />
        </TableContainer>
    );
};

export default OrderTable;